import React, { useState, memo } from "react";
import {
    Copy, Check, Play,
    FileText, FileArchive, FileCode, FileSpreadsheet, FileImage, FileVideo, FileAudio, File as FileIcon,
} from "lucide-react";
import MarkdownMessage from "./MarkdownMessage";
import MediaViewer from "./MediaViewer";
import MarkdownViewerModal from "./MarkdownViewerModal";

function MessageBubbleImpl({ msg }) {
    const isUser = msg.role === "user";
    const [copied, setCopied] = useState(false);
    const [viewer, setViewer] = useState({ open: false, index: 0 });

    const copyText = async () => {
        try {
            await navigator.clipboard.writeText(msg.content || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* ignore */
        }
    };

    // Separa anexos de mídia (imagem/vídeo) dos arquivos.
    // Filtra "pasted_content_*.txt": arquivo criado automaticamente pela Manus
    // quando o texto enviado é longo — não é um anexo real do usuário
    // (cobre também mensagens antigas já salvas no banco).
    const allAttachments = (msg.attachments || []).filter(
        (a) => !String(a?.filename || a?.name || "").toLowerCase().startsWith("pasted_content")
    );
    const mediaItems = allAttachments.filter((a) => isImage(a) || isVideo(a));
    const audioItems = allAttachments.filter((a) => isAudio(a));
    const fileItems = allAttachments.filter((a) => !isImage(a) && !isVideo(a) && !isAudio(a));

    const openViewer = (item) => {
        const idx = mediaItems.findIndex((m) => m.url === item.url);
        setViewer({ open: true, index: Math.max(0, idx) });
    };

    if (isUser) {
        const hasContent = !!(msg.content && msg.content.trim());
        return (
            <>
                <div className="flex flex-col items-end gap-1.5" data-testid="message-user">
                    {hasContent && (
                        <div className="flex items-end gap-1.5 w-full justify-end">
                            <button
                                type="button"
                                onClick={copyText}
                                data-testid="copy-user"
                                className="p-1.5 rounded-sm text-muted-foreground hover:text-primary hover:bg-muted/60 self-end mb-1 transition-colors shrink-0"
                                aria-label="Copiar mensagem"
                            >
                                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                            <div className="bg-muted/80 px-4 py-3 rounded-lg rounded-tr-sm max-w-[85%] border border-border/50">
                                <div className="prose-chat text-sm">{msg.content}</div>
                            </div>
                        </div>
                    )}
                    {audioItems.length > 0 && (
                        <div className="w-full flex justify-end"><AudioList list={audioItems} /></div>
                    )}
                    {mediaItems.length > 0 && (
                        <div className="w-full flex justify-end"><MediaGrid list={mediaItems} onOpen={openViewer} /></div>
                    )}
                    {fileItems.length > 0 && (
                        <div className="w-full flex justify-end"><FilesList list={fileItems} /></div>
                    )}
                </div>
                <MediaViewer items={mediaItems} startIndex={viewer.index} open={viewer.open} onClose={() => setViewer({ open: false, index: 0 })} />
            </>
        );
    }

    // Remove o bloco [SUGESTOES]...[/SUGESTOES] do texto (feature de sugestões
    // foi removida; o strip continua para não exibir o bloco em mensagens antigas).
    const { text: assistantText } = parseSuggestions(msg.content || "");

    return (
        <>
            <div className="flex flex-col" data-testid="message-assistant">
                <MarkdownMessage content={assistantText} />
                {audioItems.length > 0 && <AudioList list={audioItems} />}
                {mediaItems.length > 0 && <MediaGrid list={mediaItems} onOpen={openViewer} />}
                {fileItems.length > 0 && <FilesList list={fileItems} />}
                <div className="mt-2 flex items-center gap-1">
                    <button
                        type="button"
                        onClick={copyText}
                        className="flex items-center gap-1 text-[11px] font-mono text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-sm hover:bg-muted/60"
                        data-testid="copy-assistant"
                    >
                        {copied ? (<><Check className="w-3 h-3" /> Copiado</>) : (<><Copy className="w-3 h-3" /> Copiar</>)}
                    </button>
                </div>
            </div>
            <MediaViewer items={mediaItems} startIndex={viewer.index} open={viewer.open} onClose={() => setViewer({ open: false, index: 0 })} />
        </>
    );
}

const MessageBubble = memo(MessageBubbleImpl, (prev, next) => {
    const a = prev.msg, b = next.msg;
    if (a === b) return true;
    return (
        a.role === b.role &&
        a.content === b.content &&
        (a.attachments?.length || 0) === (b.attachments?.length || 0)
    );
});
export default MessageBubble;

// Extrai o bloco [SUGESTOES]a|b|c[/SUGESTOES] do texto da IA
export function parseSuggestions(content) {
    const re = /\[SUGEST(?:Õ|O)ES\]([\s\S]*?)\[\/SUGEST(?:Õ|O)ES\]/i;
    const m = re.exec(content || "");
    if (!m) return { text: content || "", suggestions: [] };
    const suggestions = m[1]
        .split("|")
        .map((s) => s.trim().replace(/^[-•\d.)\s]+/, ""))
        .filter(Boolean)
        .slice(0, 4);
    return { text: (content.replace(m[0], "")).trim(), suggestions };
}

function MediaGrid({ list, onOpen }) {
    return (
        <div className="mt-3 grid grid-cols-2 gap-2 max-w-lg" data-testid="media-grid">
            {list.map((a, i) => {
                const video = isVideo(a);
                return (
                    <button
                        key={i}
                        type="button"
                        onClick={() => onOpen(a)}
                        className="block relative group/media overflow-hidden rounded-sm border border-border bg-black"
                        data-testid={`media-thumb-${i}`}
                    >
                        {video ? (
                            <>
                                <video src={a.url} className="w-full h-48 object-cover" muted preload="metadata" />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover/media:bg-black/50 transition-colors">
                                    <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center">
                                        <Play className="w-6 h-6 text-white fill-white ml-0.5" />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <img
                                src={a.url}
                                alt={a.filename}
                                loading="eager"
                                decoding="async"
                                className="w-full h-48 object-cover group-hover/media:scale-105 transition-transform duration-300"
                            />
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                            <span className="text-[10px] font-mono text-white truncate block">{a.filename}</span>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

function isTextViewable(a) {
    const ext = ((a?.filename || "").toLowerCase().split(".").pop()) || "";
    return ["md", "txt"].includes(ext);
}

function FilesList({ list }) {
    const [viewerFile, setViewerFile] = useState(null);

    return (
        <div className="mt-3 space-y-2 max-w-lg">
            {list.map((a, i) => {
                const { Icon, color, label } = fileMeta(a);
                const viewable = isTextViewable(a);
                const inner = (
                    <>
                        <div
                            className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0"
                            style={{ backgroundColor: `${color}22`, border: `1px solid ${color}55` }}
                        >
                            <Icon className="w-5 h-5" style={{ color }} />
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                            <div className="text-sm truncate">{a.filename}</div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">{label}</div>
                        </div>
                    </>
                );
                const cls = "flex items-center gap-3 p-3 border border-border rounded-sm bg-card hover:border-primary/50 hover:bg-muted/40 transition-colors w-full";

                if (viewable) {
                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => setViewerFile(a)}
                            className={cls}
                            data-testid={`file-attachment-${i}`}
                        >
                            {inner}
                        </button>
                    );
                }
                return (
                    <a
                        key={i}
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={a.filename}
                        className={cls}
                        data-testid={`file-attachment-${i}`}
                    >
                        {inner}
                    </a>
                );
            })}
            <MarkdownViewerModal open={!!viewerFile} file={viewerFile} onClose={() => setViewerFile(null)} />
        </div>
    );
}

function AudioList({ list }) {
    return (
        <div className="mt-3 space-y-2 max-w-sm">
            {list.map((a, i) => (
                <audio
                    key={i}
                    src={a.url}
                    controls
                    preload="metadata"
                    className="w-full rounded-sm"
                    data-testid={`audio-attachment-${i}`}
                />
            ))}
        </div>
    );
}

// ----------------- helpers -----------------

function isImage(a) {
    return (a?.type === "image") || (a?.content_type || "").startsWith("image/");
}
function isVideo(a) {
    return (a?.type === "video") || (a?.content_type || "").startsWith("video/");
}
function isAudio(a) {
    return (a?.type === "audio") || (a?.content_type || "").startsWith("audio/");
}

function fileMeta(a) {
    const name = (a?.filename || "").toLowerCase();
    const ext = name.split(".").pop() || "";
    const ct = (a?.content_type || "").toLowerCase();

    // Zip / archive
    if (["zip", "rar", "7z", "tar", "gz", "bz2"].includes(ext)) {
        return { Icon: FileArchive, color: "#EAB308", label: ext.toUpperCase() };
    }
    // PDF
    if (ext === "pdf" || ct.includes("pdf")) {
        return { Icon: FileText, color: "#EF4444", label: "PDF" };
    }
    // Word
    if (["doc", "docx", "odt", "rtf"].includes(ext)) {
        return { Icon: FileText, color: "#3B82F6", label: ext.toUpperCase() };
    }
    // Excel
    if (["xls", "xlsx", "csv", "ods"].includes(ext)) {
        return { Icon: FileSpreadsheet, color: "#22C55E", label: ext.toUpperCase() };
    }
    // PowerPoint
    if (["ppt", "pptx", "odp"].includes(ext)) {
        return { Icon: FileText, color: "#F97316", label: ext.toUpperCase() };
    }
    // Código
    if (["js", "jsx", "ts", "tsx", "py", "go", "rs", "java", "c", "cpp", "h", "rb", "php", "sh", "html", "css", "json", "xml", "yaml", "yml", "md"].includes(ext)) {
        return { Icon: FileCode, color: "#A855F7", label: ext.toUpperCase() };
    }
    // Áudio
    if (ct.startsWith("audio/") || ["mp3", "wav", "ogg", "flac", "m4a", "aac"].includes(ext)) {
        return { Icon: FileAudio, color: "#EC4899", label: ext.toUpperCase() || "AUDIO" };
    }
    // Vídeo (não deveria vir aqui, mas fallback)
    if (ct.startsWith("video/") || ["mp4", "webm", "mov", "avi", "mkv"].includes(ext)) {
        return { Icon: FileVideo, color: "#06B6D4", label: ext.toUpperCase() };
    }
    // Imagem (fallback)
    if (ct.startsWith("image/")) {
        return { Icon: FileImage, color: "#06B6D4", label: ext.toUpperCase() || "IMG" };
    }
    // Texto
    if (["txt", "log"].includes(ext)) {
        return { Icon: FileText, color: "#94A3B8", label: ext.toUpperCase() };
    }
    return { Icon: FileIcon, color: "#94A3B8", label: ext.toUpperCase() || "FILE" };
}

export function TypingIndicator() {
    return (
        <div className="flex flex-col fade-in" data-testid="typing-indicator">
            <span className="text-shimmer text-sm font-medium">Pensando…</span>
        </div>
    );
}
